import { useState } from 'react';

import LaunchIcon from '@mui/icons-material/Launch';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import MinorExtDetails from './MinorExtDetails';
import SubToExtButton from './SubToExtButton';
import extPlaceholder from '../../../assets/ext-placeholder.png';
import { PRIVATE_ROUTE } from '../../../routes/routes';
import { currencyFormat } from '../../../utils/paymentUtils';
import { formatExtensionType } from '../../../utils/utils';
import Text from '../../elements/text/Text';
import Badge from '../../sections/chips/Badge';
import MarkdownReader from '../../sections/markdown-reader/MarkdownReader';

const StyledExtensionWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
  '& .MuiAvatar-img': {
    objectFit: 'contain',
  },
}));

const StyledIconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '1em',
  order: 1,
  [theme.breakpoints.down('sm')]: {
    order: 2,
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 200,
  height: 200,
  alignSelf: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    alignSelf: 'center',
  },
  border: '1px solid lightgray',
}));

const StyledTitleWrapper = styled('div')(({ theme }) => ({
  marginLeft: '1em',
  order: 2,
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    textAlign: 'center',
    order: 1,
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100],
  fontWeight: 'bold',
  width: '100%',
}));

const StyledDescriptionWrapper = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledReturnButtonWrapper = styled('div')(({ theme }) => ({
  marginLeft: 'auto',
  order: 3,
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    flexGrow: 1,
    '& > .MuiButton-text': {
      width: '100%',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginY: '2em',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const ExtensionDetails = ({ extension, fetchProjects, onSubscribe }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [projects, setProjects] = useState([]);

  const handleMenuOpen = async () => {
    const projectRes = await fetchProjects();
    setProjects(projectRes);
  };

  const handleSubscribe = async (projectId) => {
    await onSubscribe(projectId);
  };

  const {
    name,
    icon,
    type,
    description,
    descriptionUrl,
    price,
    free,
    details,
  } = extension;

  const extensionType = formatExtensionType(type);
  const handleReturnToLibrary = () => navigate(PRIVATE_ROUTE.EXTENSIONS);

  return (
    <>
      <StyledExtensionWrapper>
        <StyledIconWrapper>
          <StyledAvatar
            variant="square"
            alt={name}
            src={icon || extPlaceholder}
          />
          <Box sx={{ my: 1 }}>
            <StyledChip
              label={
                free
                  ? t('billing.free', { ns: 'project' })
                  : currencyFormat(price)
              }
            />
          </Box>
          <SubToExtButton
            projects={projects}
            onMenuOpen={handleMenuOpen}
            onSubscribe={handleSubscribe}
          />
        </StyledIconWrapper>
        <StyledTitleWrapper>
          <Box sx={{ mb: 2 }}>
            <Badge
              label={extensionType.display}
              color={extensionType.className}
            />
            <Text title>{name}</Text>
          </Box>
          {!isXsScreen && <MinorExtDetails extension={extension} />}
        </StyledTitleWrapper>
        <Box sx={{ order: 4 }}>
          {isXsScreen && <MinorExtDetails extension={extension} />}
        </Box>
        <StyledReturnButtonWrapper>
          <Button variant="text" onClick={handleReturnToLibrary}>
            {t('backToExtensionList', { ns: 'buttons' })}
          </Button>
        </StyledReturnButtonWrapper>
      </StyledExtensionWrapper>
      {description && (
        <StyledDescriptionWrapper>
          <Text>{description}</Text>
        </StyledDescriptionWrapper>
      )}
      {descriptionUrl && (
        <StyledButton
          variant="text"
          target="_blank"
          href={descriptionUrl}
          rel="noreferrer"
          endIcon={<LaunchIcon />}
        >
          {t('readMoreOnLink', { ns: 'buttons' })}
        </StyledButton>
      )}
      {details && <MarkdownReader markdown={details} />}
    </>
  );
};

ExtensionDetails.propTypes = {
  extension: PropTypes.object.isRequired,
  fetchProjects: PropTypes.func.isRequired,
  onSubscribe: PropTypes.func.isRequired,
};

export default ExtensionDetails;
